import { useEffect, useState } from 'react';
import {
  COMPANY_ROLES,
  COMPANY_ROLE_HINT,
  COMPANY_ROLE_LABEL,
  type AdminCompanyMemberRow,
  type CompanyRole,
} from 'shared';
import { api } from './api';
import { alert, confirm } from './telegram';

/**
 * Состав компании в админке.
 *
 * Существует потому, что внутри компании роли раздаёт владелец, а у
 * компании без владельца распорядиться ими некому вовсе: привязка ИНН в
 * профиле места не создаёт, а роль живёт только в месте. Получается
 * замкнутый круг, разорвать который может только администратор.
 *
 * Здесь же сведены два множества, которые в остальной системе не
 * пересекаются: оформленные местом и привязанные по ИНН. Второй случай
 * до сих пор не показывался нигде — человек числился в компании и не
 * имел никакой роли, и понять это по интерфейсу было нельзя.
 */
export default function AdminCompanyMembers({ companyId }: { companyId: string }) {
  const [items, setItems] = useState<AdminCompanyMemberRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);

  async function load() {
    setLoading(true);
    try {
      setItems((await api.admin.companyMembers(companyId)).items);
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  async function patch(
    userId: string,
    data: Parameters<typeof api.admin.updateCompanyMember>[2],
  ) {
    setBusy(userId);
    try {
      await api.admin.updateCompanyMember(companyId, userId, data);
      // Перечитываем весь состав, а не правим строку на месте: смена
      // владельца меняет роль и у прежнего, и увидеть это надо сразу.
      await load();
    } catch (e) {
      await alert((e as Error).message);
    } finally {
      setBusy(null);
    }
  }

  if (loading) return <div className="empty">Загрузка состава…</div>;
  if (items.length === 0) {
    return <div className="empty">В компании нет ни одного человека.</div>;
  }

  const hasOwner = items.some((m) => m.seat && m.active && m.roles.includes('OWNER'));

  return (
    <div className="crew-admin">
      {/* Отсутствие владельца — не мелочь в списке свойств, а причина, по
          которой компания не работает: некому нанимать, раздавать права и
          подписывать документы. Поэтому строкой во всю ширину, а не
          значком среди прочих. */}
      {!hasOwner && (
        <div className="warn-note">
          У компании нет владельца. Никто внутри неё не может нанимать людей и
          раздавать права — назначьте владельца здесь.
        </div>
      )}

      {items.map((m) => (
        <div key={m.userId} className="crew-admin__row">
          <div className="crew-admin__who">
            <strong>{m.name}</strong>

            <div className="crew-admin__tags">
              {m.seat ? (
                m.roles.map((r) => (
                  <span
                    key={r}
                    className={r === 'OWNER' ? 'badge badge--verified' : 'badge'}
                    title={COMPANY_ROLE_HINT[r]}
                  >
                    {COMPANY_ROLE_LABEL[r]}
                  </span>
                ))
              ) : (
                /* Ровно тот случай, из-за которого экран и понадобился:
                   человек привязал ИНН в профиле, числится в компании и
                   не имеет роли — потому что места ему никто не завёл. */
                <span className="badge badge--warn">привязан по ИНН, места нет</span>
              )}
              {m.seat && !m.active && <span className="badge badge--warn">уволен</span>}
              {m.banned && <span className="badge badge--danger">забанен</span>}
              {m.ordersActive > 0 && (
                <span className="badge">в рейсе: {m.ordersActive}</span>
              )}
            </div>

            <div className="meta">
              {m.phone ? <a href={`tel:${m.phone}`}>{m.phone}</a> : 'телефон не указан'}
              {m.login ? ` · логин ${m.login}` : ''}
              {m.tgUsername ? ` · @${m.tgUsername}` : ''}
              {m.email ? ` · ${m.email}` : ''}
            </div>
            <div className="meta">
              {m.joinedAt
                ? `в компании с ${new Date(m.joinedAt).toLocaleDateString('ru-RU')}`
                : 'места в компании нет'}
              {m.attachedByInn ? ' · ИНН компании стоит в профиле' : ''}
            </div>
          </div>

          <div className="crew-admin__controls">
            {COMPANY_ROLES.map((r) => (
              <label key={r} className="check" title={COMPANY_ROLE_HINT[r]}>
                <input
                  type="checkbox"
                  disabled={busy === m.userId}
                  checked={m.roles.includes(r)}
                  onChange={(e) => {
                    const next: CompanyRole[] = e.target.checked
                      ? [...m.roles, r]
                      : m.roles.filter((x) => x !== r);
                    // Снятие последней роли равносильно «убрать из компании»,
                    // и спрашиваем об этом прямо, а не отменяем молча.
                    if (next.length === 0) {
                      void (async () => {
                        if (
                          await confirm(
                            `Снять последнюю роль — значит убрать ${m.name} из компании. Убрать?`,
                          )
                        ) {
                          await patch(m.userId, { roles: [] });
                        }
                      })();
                      return;
                    }
                    void patch(m.userId, { roles: next });
                  }}
                />
                {COMPANY_ROLE_LABEL[r]}
              </label>
            ))}

            <label className="check" title="Без этого человека нет в подборе экипажа">
              <input
                type="checkbox"
                disabled={busy === m.userId || !m.seat}
                checked={m.canDrive}
                onChange={(e) => void patch(m.userId, { canDrive: e.target.checked })}
              />
              За руль
            </label>
            <label className="check" title="Видит суммы сделки">
              <input
                type="checkbox"
                disabled={busy === m.userId || !m.seat}
                checked={m.canSeeRates}
                onChange={(e) => void patch(m.userId, { canSeeRates: e.target.checked })}
              />
              Видит ставки
            </label>
            <label className="check" title="Может сам откликаться на грузы от имени компании">
              <input
                type="checkbox"
                disabled={busy === m.userId || !m.seat}
                checked={m.canBook}
                onChange={(e) => void patch(m.userId, { canBook: e.target.checked })}
              />
              Берёт грузы
            </label>

            {!m.roles.includes('OWNER') && (
              <button
                type="button"
                className="secondary"
                disabled={busy === m.userId}
                onClick={() => {
                  void (async () => {
                    // Владелец один: прежний станет диспетчером. Об этом
                    // говорим до нажатия, а не после.
                    const q = hasOwner
                      ? `Назначить ${m.name} владельцем? Прежний владелец станет диспетчером.`
                      : `Назначить ${m.name} владельцем компании?`;
                    if (await confirm(q)) {
                      await patch(m.userId, {
                        roles: [...new Set<CompanyRole>([...m.roles, 'OWNER', 'MANAGER'])],
                        canSeeRates: true,
                        canBook: true,
                        active: true,
                      });
                    }
                  })();
                }}
              >
                Назначить владельцем
              </button>
            )}

            {m.seat && (
              <button
                type="button"
                className="secondary"
                disabled={busy === m.userId}
                onClick={() => void patch(m.userId, { active: !m.active })}
              >
                {m.active ? 'Уволить' : 'Вернуть в штат'}
              </button>
            )}

            {m.attachedByInn && (
              <button
                type="button"
                className="secondary"
                disabled={busy === m.userId}
                onClick={() => {
                  void (async () => {
                    if (
                      await confirm(
                        `Убрать ИНН этой компании из профиля ${m.name}? Место в компании останется.`,
                      )
                    ) {
                      await patch(m.userId, { detach: true });
                    }
                  })();
                }}
              >
                Отвязать ИНН
              </button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
